// src/pages/InfoPage.js
// Pages d'information publiques (tarifs, complémentarité MonAideCyber), contenu dans src/data/infoPages.js.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, CheckCircle, Minus, ArrowRight, ChevronDown, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import infoPages from '../data/infoPages';
import TRANSLATIONS from '../data/landingContent';
import checkPages from '../data/checkPages';
import useSEO from '../hooks/useSEO';
import NotFound from './NotFound';

const PLAN_STYLES = {
  blue: 'border-blue-500/40 text-blue-400',
  green: 'border-green-500 text-green-400',
  purple: 'border-purple-500/40 text-purple-400',
};

function Cell({ value }) {
  if (value === true) return <CheckCircle className="w-5 h-5 text-green-500 mx-auto" aria-label="Inclus" />;
  if (value === false) return <Minus className="w-5 h-5 text-zinc-700 mx-auto" aria-label="Non inclus" />;
  return <span className="text-zinc-200 font-mono text-xs">{value}</span>;
}

export default function InfoPage({ slug }) {
  const page = infoPages.find((p) => p.slug === slug);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  useSEO(`/${slug}`, { title: page?.seoTitle, description: page?.description });
  if (!page) return <NotFound />;

  const plans = TRANSLATIONS.fr.plans;
  const start = () => navigate(user ? '/audits/new' : '/register');

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <header className="border-b border-zinc-800 bg-black/80">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-500" />
            <span className="font-mono text-lg font-black uppercase tracking-tighter">CyberGuard</span>
          </Link>
          <nav className="flex items-center gap-5">
            <Link to="/blog" className="hidden sm:inline text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-widest">Blog</Link>
            <Link to="/login" className="hidden sm:inline text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-widest">Connexion</Link>
            <button onClick={start} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 font-mono text-xs uppercase tracking-widest">Commencer</button>
          </nav>
        </div>
      </header>

      <main className={`${page.showPlans ? 'max-w-5xl' : 'max-w-3xl'} mx-auto px-4 py-16`}>
        <p className="font-mono text-xs uppercase tracking-widest text-blue-400 mb-4">{page.kicker}</p>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-6">{page.h1}</h1>
        <div className="max-w-3xl">
          {page.intro.map((p, i) => <p key={i} className="text-zinc-300 text-lg leading-relaxed mb-4">{p}</p>)}
        </div>

        {page.showPlans && (
          <>
            <section className="mt-12 grid gap-6 md:grid-cols-3" data-testid="pricing-plans">
              {plans.map((p) => (
                <div key={p.name} className={`relative bg-zinc-900 border p-6 flex flex-col ${p.popular ? 'border-green-500' : 'border-zinc-800'}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-black text-[10px] font-black font-mono px-3 py-1 uppercase tracking-widest inline-flex items-center gap-1">
                      <Star className="w-3 h-3" /> Recommandé
                    </span>
                  )}
                  <h2 className={`text-lg font-black font-mono uppercase ${PLAN_STYLES[p.color]?.split(' ')[1] || ''}`}>{p.name}</h2>
                  <p className="mt-3 mb-5"><span className="text-4xl font-black font-mono">{p.price} €</span><span className="text-zinc-500 font-mono text-sm"> /mois</span></p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> {f}</li>
                    ))}
                  </ul>
                  <button onClick={start} className="bg-blue-600 hover:bg-blue-700 px-4 py-3 font-mono text-xs uppercase tracking-widest">Commencer par l'audit offert</button>
                </div>
              ))}
            </section>

            <section className="mt-16">
              <h2 className="text-2xl font-bold mb-6">{page.comparisonTitle}</h2>
              <div className="overflow-x-auto border border-zinc-800">
                <table className="w-full text-sm min-w-[560px]" data-testid="pricing-comparison">
                  <thead className="bg-zinc-900 text-zinc-400 font-mono text-xs uppercase">
                    <tr>
                      <th className="text-left p-4 font-normal">Fonctionnalité</th>
                      {plans.map((p) => <th key={p.name} className="p-4 font-normal w-28">{p.name}<br /><span className="text-white">{p.price} €</span></th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {page.comparison.map((row) => (
                      <tr key={row.label} className="border-t border-zinc-800">
                        <td className="p-4 text-zinc-300">{row.label}</td>
                        {row.values.map((v, i) => <td key={i} className="p-4 text-center"><Cell value={v} /></td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        <div className="max-w-3xl">
          {page.sections.map((s) => (
            <section key={s.title} className="mt-16">
              <h2 className="text-2xl font-bold mb-6">{s.title}</h2>
              {(s.paragraphs || []).map((p, i) => <p key={i} className="text-zinc-300 leading-relaxed mb-4">{p}</p>)}
              {s.items && (
                <dl className="space-y-4">
                  {s.items.map((it) => (
                    <div key={it.term} className="bg-zinc-900 border border-zinc-800 p-5">
                      <dt className="font-mono font-bold text-blue-400 mb-1">{it.term}</dt>
                      <dd className="text-zinc-300 leading-relaxed">{it.text}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          ))}

          <section className="mt-16">
            <h2 className="text-2xl font-bold mb-6">Questions fréquentes</h2>
            <div className="divide-y divide-zinc-800 border-y border-zinc-800">
              {page.faqs.map((f, i) => (
                <div key={f.q}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}
                    className="w-full flex items-center justify-between gap-4 py-4 text-left font-semibold">
                    {f.q}
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && <p className="pb-4 text-zinc-300 leading-relaxed">{f.a}</p>}
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 bg-zinc-900 border border-zinc-800 p-8 text-center">
            <h2 className="text-2xl font-bold mb-3">{page.ctaTitle}</h2>
            <p className="text-zinc-400 mb-6">Premier audit offert, sans carte bancaire. Résultat en moins de 2 minutes.</p>
            <button onClick={start} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 font-mono text-sm uppercase tracking-widest">
              {page.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </section>

          {page.related?.length > 0 && (
            <section className="mt-12">
              <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">À lire aussi</h2>
              <ul className="space-y-2">
                {page.related.map((r) => <li key={r.href}><Link to={r.href} className="text-blue-400 hover:underline">{r.label}</Link></li>)}
              </ul>
            </section>
          )}

          {page.sources?.length > 0 && (
            <section className="mt-12">
              <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Sources</h2>
              <ul className="space-y-1 text-sm">
                {page.sources.map((s) => <li key={s.href}><a href={s.href} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-white underline">{s.label}</a></li>)}
              </ul>
            </section>
          )}

          <section className="mt-12">
            <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Vérifications</h2>
            <ul className="flex flex-wrap gap-3">
              {checkPages.map((c) => (
                <li key={c.slug}><Link to={`/${c.slug}`} className="inline-block border border-zinc-800 hover:border-blue-500/50 px-4 py-2 text-sm text-zinc-300 hover:text-white">{c.navLabel}</Link></li>
              ))}
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-6 justify-center">
          {[['/', 'Accueil'], ['/tarifs', 'Tarifs'], ['/blog', 'Blog'], ['/cgv', 'CGU'], ['/mentions-legales', 'Mentions légales'], ['/confidentialite', 'Confidentialité']].map(([to, label]) => (
            <Link key={to} to={to} className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">{label}</Link>
          ))}
        </div>
      </footer>
    </div>
  );
}
